import { actionTypes as dataFetchActionTypes } from '@buffer-mono/async-data-fetch'
import type { Middleware, UnknownAction } from '@reduxjs/toolkit'
import type { Channel as PusherChannel } from 'pusher-js'
import { getSavedVideoAudioCodec } from '~publish/helpers/videoAudioMetadata'

import AppDispatcher from '~publish/legacy/composer/composer/dispatcher'
import { ActionTypes } from '~publish/legacy/composer/composer/state/ActionTypes'
import { actionTypes as organizationsActionTypes } from '~publish/legacy/organizations/reducer'
import { noteAdded, noteDeleted, noteUpdated } from '~publish/legacy/post/slice'
import type { PublishingNote } from '~publish/legacy/post/graphqlTypes'
import { PostEvents } from '~publish/legacy/post/types'
import type { RpcMediaVideo } from '~publish/legacy/post/types'
import type { AppDispatch, RootState } from '~publish/legacy/store'
import { isLegacyAction } from '~publish/legacy/store/isAction'
import { actionTypes as profileSidebarActionTypes } from '~publish/legacy/profile-sidebar/reducer'
import { IdeaEvents } from '~publish/pages/Create/types'
import { pusher } from '~publish/services/pusher'

type PusherAction = UnknownAction & {
  profileId?: string
  selected?: { id: string }
  result?: { id: string }
}

const isPusherAction = (action: unknown): action is PusherAction =>
  isLegacyAction(action) &&
  (action.profileId === undefined || typeof action.profileId === 'string') &&
  (action.selected === undefined ||
    (typeof action.selected === 'object' &&
      action.selected !== null &&
      typeof action.selected.id === 'string')) &&
  (action.result === undefined ||
    (typeof action.result === 'object' &&
      action.result !== null &&
      typeof action.result.id === 'string'))

type ProfileChannels = {
  updates: PusherChannel
}

type OrganizationNoteEvent = {
  profile_id: string
  update_id: string
  update: {
    notes: PublishingNote[]
  }
}

type TranscodeDoneEvent = {
  upload_id: string
  media: {
    thumbnail: string
    video: Pick<RpcMediaVideo, 'title' | 'details' | 'thumbnails'>
  }
}

const bindProfileUpdateEvents = (
  channel: PusherChannel,
  profileId: string,
  dispatch: AppDispatch,
): void => {
  channel.bind('queue_paused', (paused: boolean) => {
    dispatch({
      type: profileSidebarActionTypes.PUSHER_PROFILE_PAUSED_STATE,
      paused,
      profileId,
    })
  })
}

const channelsByProfileId: Record<string, ProfileChannels> = {}
const setupProfilePusherEvents = (
  { profileId }: { profileId?: string },
  pusherClient: typeof pusher,
  dispatch: AppDispatch,
): void => {
  if (profileId) {
    // If the profile is not subscribed to any channels, subscribes to private-updates channel:
    const newProfileChannels = channelsByProfileId[profileId] || {
      updates: pusherClient.subscribe(`private-updates-${profileId}`),
    }

    channelsByProfileId[profileId] = newProfileChannels

    bindProfileUpdateEvents(
      channelsByProfileId[profileId].updates,
      profileId,
      dispatch,
    )
  }
}

const bindOrganizationEvents = (
  orgChannel: PusherChannel,
  dispatch: AppDispatch,
): void => {
  orgChannel.bind(PostEvents.POST_NOTE_ADDED, (data: OrganizationNoteEvent) => {
    dispatch(
      noteAdded({
        profileId: data.profile_id,
        postId: data.update_id,
        notes: data.update.notes,
      }),
    )
  })
  orgChannel.bind(
    PostEvents.POST_NOTE_UPDATED,
    (data: OrganizationNoteEvent) => {
      dispatch(
        noteUpdated({
          profileId: data.profile_id,
          postId: data.update_id,
          notes: data.update.notes,
        }),
      )
    },
  )
  orgChannel.bind(
    PostEvents.POST_NOTE_DELETED,
    (data: OrganizationNoteEvent) => {
      dispatch(
        noteDeleted({
          profileId: data.profile_id,
          postId: data.update_id,
          notes: data.update.notes,
        }),
      )
    },
  )
  Object.values(IdeaEvents).forEach((eventName) => {
    orgChannel.bind(eventName, (data: unknown) => {
      dispatch({
        eventName,
        type: `PUSHER_EVENT/${eventName}`,
        data,
      })
    })
  })
}

const bindUserEvents = (userChannel: PusherChannel): void => {
  // Video transcoded
  userChannel.bind('transcode_done', (data: TranscodeDoneEvent) => {
    AppDispatcher.handlePusherAction({
      actionType: ActionTypes.COMPOSER_VIDEO_PROCESSED,
      processedVideoData: {
        uploadId: data.upload_id,
        name: data.media.video.title,
        duration: data.media.video.details?.duration,
        durationMs: data.media.video.details?.duration_millis,
        audioCodec: getSavedVideoAudioCodec(data.media.video.details),
        size: data.media.video.details?.file_size,
        width: data.media.video.details?.width,
        height: data.media.video.details?.height,
        url: data.media.video.details?.transcoded_location,
        originalUrl: data.media.video.details?.location,
        thumbnail: data.media.thumbnail,
        availableThumbnails: data.media.video.thumbnails,
      },
    })
  })
}

/**
 * Middleware
 */
const middleware: Middleware<object, RootState, AppDispatch> = ({
  dispatch,
}) => {
  const subscribeOrganizationEvents = (
    organizationId: string | undefined,
  ): void => {
    if (organizationId) {
      const channelName = `private-updates-org-${organizationId}`
      const orgChannel = pusher.subscribe(channelName)
      bindOrganizationEvents(orgChannel, dispatch)
    }
  }

  const subscribeUserEvents = (userId: string | undefined): void => {
    if (userId) {
      const channelName = `private-updates-${userId}`
      const userChannel = pusher.subscribe(channelName)
      bindUserEvents(userChannel)
    }
  }

  return (next) =>
    (action): void => {
      next(action)
      if (!isPusherAction(action)) return

      switch (action.type) {
        case profileSidebarActionTypes.SELECT_PROFILE:
          setupProfilePusherEvents(action, pusher, dispatch)
          break
        case organizationsActionTypes.ORGANIZATION_SELECTED:
          subscribeOrganizationEvents(action.selected?.id)
          break
        case `user_${dataFetchActionTypes.FETCH_SUCCESS}`:
          subscribeUserEvents(action.result?.id)
          break
        default:
          break
      }
    }
}

export default middleware
